import Barcode from "react-barcode";
import { QRCodeSVG } from "qrcode.react";
import { Download } from "lucide-react";

/**
 * BarcodePanel — shows Code128 barcode + QR code for a job
 * Used on JobDetail, PrintInvoice, TrackJob pages
 */
export default function BarcodePanel({ jobNo, trackingUrl, compact = false }) {
  if (!jobNo) return null;

  const handleDownload = () => {
    const svg = document.getElementById(`barcode-svg-${jobNo}`);
    if (!svg) return;
    const data = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([data], { type: "image/svg+xml" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `${jobNo}-barcode.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (compact) {
    return (
      <div className="flex items-center gap-4">
        <div id={`barcode-svg-${jobNo}`}>
          <Barcode
            value={jobNo}
            width={1.2}
            height={40}
            fontSize={10}
            margin={4}
            background="transparent"
          />
        </div>
        {trackingUrl && (
          <QRCodeSVG
            value={trackingUrl}
            size={48}
            level="M"
            includeMargin={false}
          />
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200/70 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Shipment Barcode
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">Scan to track — attach to device / parcel</p>
        </div>
        <button
          onClick={handleDownload}
          className="flex items-center gap-1.5 text-xs text-green-700 hover:text-green-800 border border-green-200 px-3 py-1.5 rounded-lg hover:bg-green-50 transition-colors"
        >
          <Download size={12} /> Save Barcode
        </button>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-6">
        {/* Code128 Barcode */}
        <div className="flex flex-col items-center">
          <div className="border border-slate-200 rounded-xl p-3 bg-white" id={`barcode-svg-${jobNo}`}>
            <Barcode
              value={jobNo}
              width={1.8}
              height={60}
              fontSize={13}
              margin={6}
              background="#ffffff"
            />
          </div>
          <p className="text-xs text-slate-400 mt-2">Code-128 Barcode</p>
        </div>

        <div className="hidden sm:block w-px h-20 bg-slate-200" />

        {/* QR Code */}
        {trackingUrl && (
          <div className="flex flex-col items-center">
            <div className="border border-slate-200 rounded-xl p-3 bg-white">
              <QRCodeSVG
                value={trackingUrl}
                size={80}
                level="M"
                includeMargin={false}
              />
            </div>
            <p className="text-xs text-slate-400 mt-2">QR — Customer Tracking</p>
          </div>
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-500 mb-1">Job Number</p>
          <p className="font-mono text-lg font-bold text-green-700">{jobNo}</p>
          {trackingUrl && (
            <>
              <p className="text-xs text-slate-500 mt-3 mb-1">Customer Tracking Link</p>
              <a href={trackingUrl} target="_blank" rel="noreferrer"
                className="text-xs text-green-600 hover:underline break-all">
                {trackingUrl}
              </a>
            </>
          )}
          <p className="text-xs text-slate-400 mt-3">
            Attach this barcode to the device/packaging to ensure correct identification throughout the repair journey.
          </p>
        </div>
      </div>
    </div>
  );
}
